import { Component } from "react";

/**
 * Catches lazy-chunk load failures (common after a redeploy when the user's
 * cached `index.html` references chunk hashes that no longer exist on the CDN).
 *
 * Strategy:
 *   1. First failure  →  silently force-reload once (sessionStorage flag prevents loop).
 *   2. Second failure →  render a visible "Reload page" UI so the user is never
 *      left staring at a black screen.
 *
 * Why this matters: without this boundary a ChunkLoadError bubbles up past
 * <Suspense> and unmounts the whole React tree, leaving only the dark `bg-[#050505]`
 * body — which is what users see as a "black page" on /redeem.
 */
class ChunkErrorBoundary extends Component {
  state = { hasError: false, isChunkError: false };

  static getDerivedStateFromError(error) {
    const msg = (error && (error.message || error.toString())) || "";
    const isChunkError =
      error?.name === "ChunkLoadError" ||
      /Loading chunk [\d]+ failed/i.test(msg) ||
      /Loading CSS chunk [\d]+ failed/i.test(msg) ||
      /Failed to fetch dynamically imported module/i.test(msg) ||
      /Importing a module script failed/i.test(msg);
    return { hasError: true, isChunkError };
  }

  componentDidCatch(error) {
    if (this.state.isChunkError) {
      const reloadedOnce = sessionStorage.getItem("jr_chunk_reload_attempted");
      if (!reloadedOnce) {
        try { sessionStorage.setItem("jr_chunk_reload_attempted", "1"); } catch {}
        // Aggressive cache-bust:
        //   1. Unregister any stale service workers that might be serving old chunks.
        //   2. Reload via window.location.replace with a `?_v=` cache-buster so the
        //      CDN can't return the stale index.html for the new URL.
        try {
          if ("serviceWorker" in navigator) {
            navigator.serviceWorker.getRegistrations().then((regs) => {
              regs.forEach((r) => r.unregister().catch(() => {}));
            }).catch(() => {});
          }
        } catch {}
        try {
          if (window.caches && window.caches.keys) {
            window.caches.keys().then((keys) => {
              keys.forEach((k) => window.caches.delete(k).catch(() => {}));
            }).catch(() => {});
          }
        } catch {}
        const sep = window.location.search.includes("?") ? "&" : "?";
        const url = window.location.pathname + window.location.search + sep + "_v=" + Date.now();
        // Use replace to avoid adding to history.
        window.location.replace(url);
      } else {
        // 2nd chunk failure — silent fallback to "/" instead of the
        // misleading "We need to refresh" modal (which users complained
        // about). Going home with a cache-buster always lands them on a
        // working page from a freshly-fetched index.html.
        try { sessionStorage.removeItem("jr_chunk_reload_attempted"); } catch {}
        window.location.replace("/?_v=" + Date.now() + "&recovered=1");
      }
    } else {
      // Non-chunk runtime error — log but DON'T trip the "stale bundle"
      // modal (rendering returns children below). Clear the reload-attempt
      // flag so a later, genuine chunk error can re-arm.
      try { sessionStorage.removeItem("jr_chunk_reload_attempted"); } catch {}
      console.error("[ChunkErrorBoundary] runtime error (not chunk-load):", error);
    }
  }

  handleManualReload = () => {
    try { sessionStorage.removeItem("jr_chunk_reload_attempted"); } catch {}
    window.location.reload();
  };

  render() {
    // Visible fallback when a chunk load has failed. componentDidCatch
    // has already fired an in-flight reload/redirect above — this render
    // makes sure users see a friendly "Updating…" indicator during the
    // brief window before the redirect completes, instead of a blank
    // black page. Owner directive Feb 2026.
    if (this.state.hasError && this.state.isChunkError) {
      return (
        <div
          className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-[#050505] text-center px-6"
          data-testid="chunk-error-boundary-fallback"
        >
          <div className="w-12 h-12 rounded-full border-2 border-amber-400 border-t-transparent animate-spin mb-4" />
          <p className="text-amber-400 font-bold text-lg">Updating JackRoyals…</p>
          <p className="text-zinc-400 text-sm mt-2 max-w-sm">
            We're loading the latest build. This only takes a second.
          </p>
          <button
            onClick={this.handleManualReload}
            className="mt-6 text-xs text-amber-400/80 hover:text-amber-300 underline"
            data-testid="chunk-error-manual-reload"
          >
            Not loading? Tap to refresh
          </button>
        </div>
      );
    }
    // Non-chunk RUNTIME error (null-deref inside a page, etc.). Prior
    // behaviour was to `return this.props.children` — but React can't
    // remount the crashed tree, so users saw a permanent blank screen.
    // Owner directive Feb 2026: never leave the user staring at a black
    // page — always show a friendly retry surface.
    if (this.state.hasError) {
      return (
        <div
          className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-[#050505] text-center px-6"
          data-testid="app-error-boundary-fallback"
        >
          <div className="w-14 h-14 rounded-full border-2 border-red-400 flex items-center justify-center mb-4">
            <span className="text-red-400 text-2xl">!</span>
          </div>
          <p className="text-white font-bold text-lg">Something went wrong</p>
          <p className="text-zinc-400 text-sm mt-2 max-w-sm">
            The page hit an error. Refresh to reload — your balance and progress are safe.
          </p>
          <button
            onClick={this.handleManualReload}
            className="mt-6 px-5 py-2 rounded-full font-bold text-sm text-white"
            style={{ background: 'linear-gradient(135deg, #4d7cff, #7f5dff)' }}
            data-testid="app-error-refresh"
          >
            Refresh Page
          </button>
          <button
            onClick={() => window.location.replace("/lobby")}
            className="mt-3 text-xs text-zinc-400 hover:text-white underline"
            data-testid="app-error-back-to-lobby"
          >
            Back to Lobby
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ChunkErrorBoundary;
